import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Ruler, Plus, MoreVertical, FolderOpen, Copy, Trash2, Edit, LogOut } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";
import GrupoVentasuelos from "@/components/GrupoVentasuelos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await authFetch(`${API}/projects`);
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (err) {
      toast.error("Error cargando proyectos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("¿Eliminar este proyecto?")) return;
    try {
      const res = await authFetch(`${API}/projects/${projectId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setProjects((prev) => prev.filter((p) => p.project_id !== projectId));
        toast.success("Proyecto eliminado");
      }
    } catch {
      toast.error("Error eliminando proyecto");
    }
  };

  const handleDuplicate = async (projectId) => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}/duplicate`, {
        method: "POST",
      });
      if (res.ok) {
        const newProj = await res.json();
        setProjects((prev) => [newProj, ...prev]);
        toast.success("Proyecto duplicado");
      }
    } catch {
      toast.error("Error duplicando proyecto");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="dashboard-logo">
            <img src="/planimetrik-header.png" alt="Planimetrik" className="h-7 object-contain" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#4A5568] hidden sm:block">{user?.name || user?.email}</span>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#4A5568] hover:text-[#1B2B4D]"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1B2B4D]" data-testid="dashboard-title">
              Mis Proyectos
            </h1>
            <p className="text-sm text-[#4A5568] mt-1">{projects.length} proyecto{projects.length !== 1 ? "s" : ""}</p>
          </div>
          <Button
            data-testid="new-project-btn"
            onClick={() => navigate("/project/new")}
            className="bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Proyecto
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-3 border-[#0066FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="bg-white rounded-xl border border-[#E2E8F0]" data-testid="empty-state">
            <CardContent className="py-16 text-center">
              <FolderOpen className="h-12 w-12 text-[#CBD5E0] mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-[#1B2B4D] mb-2">Sin proyectos</h3>
              <p className="text-sm text-[#4A5568] mb-6">Crea tu primer proyecto para empezar a calcular materiales</p>
              <Button
                data-testid="empty-create-btn"
                onClick={() => navigate("/project/new")}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Proyecto
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((proj) => (
              <Card
                key={proj.project_id}
                className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                data-testid={`project-card-${proj.project_id}`}
                onClick={() => navigate(`/project/${proj.project_id}`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-base font-semibold text-[#1B2B4D] truncate">
                        {proj.project_name}
                      </h3>
                      <p className="text-xs text-[#4A5568] mt-1">{formatDate(proj.created_at)}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`project-menu-${proj.project_id}`}>
                          <MoreVertical className="h-4 w-4 text-[#4A5568]" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => navigate(`/project/${proj.project_id}`)} data-testid="edit-project-btn">
                          <Edit className="h-4 w-4 mr-2" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(proj.project_id)} data-testid="duplicate-project-btn">
                          <Copy className="h-4 w-4 mr-2" /> Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(proj.project_id)} className="text-red-600" data-testid="delete-project-btn">
                          <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {proj.material_type && (
                      <Badge variant="secondary" className="bg-[#E3F2FD] text-[#0066FF] border border-[#0066FF]/20 text-xs">
                        {proj.material_type}
                      </Badge>
                    )}
                    {proj.surface_m2 && (
                      <Badge variant="secondary" className="bg-[#F7FAFC] text-[#4A5568] border border-[#E2E8F0] text-xs">
                        {proj.surface_m2} m2
                      </Badge>
                    )}
                    {proj.room_type && (
                      <Badge variant="secondary" className="bg-[#FFF3E0] text-[#E65100] border border-[#FFA500]/20 text-xs">
                        {proj.room_type}
                      </Badge>
                    )}
                  </div>

                  
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Grupo Ventasuelos */}
      <GrupoVentasuelos />

      {/* Footer */}
      <footer className="bg-[#F0F4F8] py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-xs text-[#4A5568]">Planimetrik &copy; {new Date().getFullYear()} - Todos los derechos reservados.</p>
          <p className="text-xs text-[#718096] mt-0.5">A Ventasuelos SL Company</p>
        </div>
      </footer>
    </div>
  );
}
